// src/components/MapControls.js
import React from "react";
import { Box, CircularProgress, IconButton, Tooltip } from "@mui/material";
import { keyframes } from "@emotion/react";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import LocalBarIcon from "@mui/icons-material/LocalBar";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import PlacesSearch from "./PlacesSearch";
import RecenterButton from "./RecenterButton";

const quickFindWave = keyframes`
  0% { transform: translateY(0) scale(1); }
  28% { transform: translateY(-7px) scale(1.08); }
  56% { transform: translateY(2px) scale(0.98); }
  78% { transform: translateY(-2px) scale(1.02); }
  100% { transform: translateY(0) scale(1); }
`;

const QUICK_FIND_ACTIONS = [
  {
    key: "eat",
    label: "Find food nearby",
    color: "#FF6B4A",
    Icon: RestaurantIcon,
  },
  {
    key: "drink",
    label: "Find drinks nearby",
    color: "#00A7C7",
    Icon: LocalBarIcon,
  },
  {
    key: "fun",
    label: "Find fun nearby",
    color: "#5B4BFF",
    Icon: AutoAwesomeRoundedIcon,
  },
];

export default function MapControls({
  searchText,
  onSearchChange,
  onSelect,
  onRecenter,
  onQuickFind,
  onQuietQuickFindDismiss,
  onQuietQuickFindStatusClick,
  activeQuickFindKey = "",
  quietQuickFindKey,
  quietQuickFindStatus,
  quickFindCategoryStates = {},
  quickFindDisabled = false,
  recenterDisabled = false,
  sx,
  ...rest
}) {
  return (
    <Box
      data-testid="map-controls-overlay"
      sx={{
        position: "absolute",
        top: { xs: 16, md: 24 },
        left: { xs: 16, md: "50%" },
        right: { xs: 16, md: "auto" },
        transform: { xs: "none", md: "translateX(-50%)" },
        width: {
          xs: "calc(100% - 32px)",
          md: "min(680px, calc(100% - 32px), calc(100vw - 48px))",
        },
        maxWidth: "calc(100% - 32px)",
        zIndex: 12,
        ...sx,
        pointerEvents: "none",
      }}
      {...rest}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "48px minmax(0, 1fr)",
          columnGap: 1,
          alignItems: "start",
        }}
      >
        <RecenterButton
          onClick={onRecenter}
          disabled={recenterDisabled}
          sx={(theme) => ({
            gridColumn: 1,
            gridRow: 1,
            pointerEvents: "auto",
            backgroundColor: theme.colors.blue,
            color: theme.colors.white,
            border: `2px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.28)" : "rgba(255,255,255,0.72)"}`,
            opacity: recenterDisabled ? 0.55 : 1,
            "&:hover": {
              backgroundColor: theme.colors.blue,
              borderColor: theme.colors.white,
            },
          })}
        />
        <Box
          data-testid="map-controls-search"
          sx={{ gridColumn: 2, gridRow: 1, pointerEvents: "auto" }}
        >
          <PlacesSearch
            value={searchText}
            onChange={onSearchChange}
            onSelect={onSelect}
          />
        </Box>
        <Box
          data-testid="map-controls-quick-find"
          sx={{
            gridColumn: 1,
            gridRow: 2,
            display: "grid",
            gap: 1,
            mt: 1,
            pointerEvents: "auto",
          }}
        >
          {QUICK_FIND_ACTIONS.map(({ key, label, color, Icon }, index) => {
            const isActive = activeQuickFindKey === key;
            const categoryState = quickFindCategoryStates?.[key] || {};
            const categoryPhase = String(categoryState?.phase || "idle").trim().toLowerCase();
            const isCategoryLoading = categoryPhase === "loading";
            const readyCount =
              categoryPhase === "ready" && Number(categoryState?.count || 0) > 0
                ? Math.floor(Number(categoryState.count))
                : 0;
            const readyBadgeText = readyCount > 99 ? "99+" : String(readyCount);
            const isDisabled = quickFindDisabled || isCategoryLoading;
            return (
              <Tooltip key={key} title={label}>
                <Box component="span" sx={{ display: "inline-grid" }}>
                  <IconButton
                    aria-label={label}
                    aria-pressed={isActive}
                    disabled={isDisabled}
                    onClick={() => onQuickFind?.(key)}
                    sx={(theme) => ({
                      width: 48,
                      height: 48,
                      position: "relative",
                      overflow: "visible",
                      color: theme.colors.white,
                      backgroundColor: color,
                      border: isActive
                        ? `2px solid ${theme.colors.white}`
                        : `2px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.28)" : "rgba(255,255,255,0.72)"}`,
                      animation: quickFindDisabled
                        ? "none"
                        : `${quickFindWave} 760ms cubic-bezier(0.2, 0.85, 0.3, 1.2) ${360 + index * 150}ms both`,
                      opacity: isDisabled ? 0.56 : 1,
                      transition: "transform 180ms ease, opacity 180ms ease, border-color 180ms ease",
                      "&:hover": {
                        backgroundColor: color,
                        transform: isDisabled ? "none" : "scale(1.05)",
                      },
                      "@media (prefers-reduced-motion: reduce)": {
                        animation: "none",
                      },
                      "&.Mui-disabled": {
                        color: theme.colors.white,
                        backgroundColor: color,
                      },
                    })}
                  >
                    <Icon />
                    {isCategoryLoading ? (
                      <Box
                        component="span"
                        data-testid={`quick-find-${key}-loading-frame`}
                        sx={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          width: 34,
                          height: 34,
                          display: "grid",
                          placeItems: "center",
                          pointerEvents: "none",
                        }}
                      >
                        <CircularProgress
                          data-testid={`quick-find-${key}-loading`}
                          size={34}
                          thickness={3.6}
                          sx={{
                            color: "rgba(255,255,255,0.9)",
                          }}
                        />
                      </Box>
                    ) : null}
                    {readyCount > 0 ? (
                      <Box
                        component="span"
                        data-testid={`quick-find-${key}-badge`}
                        sx={(theme) => ({
                          position: "absolute",
                          top: -7,
                          right: -7,
                          minWidth: readyBadgeText.length === 1 ? 22 : 28,
                          height: 22,
                          px: readyBadgeText.length === 1 ? 0 : "6px",
                          display: "grid",
                          placeItems: "center",
                          borderRadius: "999px",
                          border: `2px solid ${theme.palette.common.white}`,
                          backgroundColor: theme.colors?.green || "#2E7D32",
                          color: theme.colors?.white || theme.palette.common.white,
                          fontSize: 11,
                          fontWeight: 950,
                          lineHeight: 1,
                          letterSpacing: 0,
                          whiteSpace: "nowrap",
                          maxWidth: "none",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.32)",
                          pointerEvents: "none",
                        })}
                      >
                        {readyBadgeText}
                      </Box>
                    ) : null}
                  </IconButton>
                </Box>
              </Tooltip>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}
